import { Component, inject, Input, OnInit } from "@angular/core";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { ColorsService } from "@arch/domain/application/services/colors.service";
import {
    CompanyEntity,
    PointEntity,
    SerieEntity,
    VisualizationEntity
} from "@arch/domain/entity";
import {
    GetPointRateConstantController,
    UpdateSerieRateController
} from "@arch/presentation/controllers";
import { HighchartsRenderComponent } from "../../highcharts-render/highcharts-render.component";

@Component({
    imports: [HighchartsRenderComponent, SharedMaterialModule, SharedDsnModule],
    templateUrl: "./chart-with-calculator.component.html",
    styleUrl: "./chart-with-calculator.component.scss"
})
export class ChartWithCalculatorComponent implements OnInit {
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) options: VisualizationOptionsType;
    @Input({ required: true }) series: SerieEntity[];
    @Input({ required: true }) points!: PointEntity[];
    @Input({ required: true }) company?: CompanyEntity;

    private updateSerieRateController = inject(UpdateSerieRateController);
    private getPointRateConstantController = inject(
        GetPointRateConstantController
    );
    private updateRateInterval: any = null;
    public currency: string = "0,01";

    ngOnInit(): void {
        this.setGoalsVisualization();
        this.setDefaultValue();
    }

    private setDefaultValue() {
        const currency = this.getPointRateConstantController.execute({
            scenarioId: this.options.scenario.props._id,
            period: this.options.period,
            date: this.options.endDate
        });
        this.currency = String(currency || 0.1);
    }

    private setGoalsVisualization(): void {
        const chartStructure = this.visualization.props.component.configs.chart;
        if (this.visualization.props.goals.length) {
            chartStructure.goals = this.visualization.props.goals.map(
                (goal: any) => {
                    const color = ColorsService.getLowerTonality(
                        chartStructure?.chart?.series?.[0]?.color ||
                            "goals.medium"
                    );
                    return {
                        type: "line",
                        _id: goal.r,
                        color: goal?.color || color,
                        dashStyle: goal?.dashStyle || "ShortDash"
                    };
                }
            );
        }
        chartStructure.series = chartStructure.series.map((s: any) => ({
            ...s,
            type: "column"
        }));
        this.visualization.setChartConfiguration(chartStructure);
    }

    public onRateChange(value: string) {
        if (this.updateRateInterval) {
            clearTimeout(this.updateRateInterval);
        }
        this.currency = value;
        this.updateRateInterval = setTimeout(() => {
            this.onUpdateRate();
        }, 1e3);
    }

    public async onUpdateRate(): Promise<void> {
        await this.updateSerieRateController.execute({
            visualization: this.visualization,
            company: this.company,
            value: Number(this.currency),
            scenarioId: this.options.scenario.props._id
        });
    }
}

export interface IChartWithCalculatorComponent {
    series: SerieEntity[];
    points: PointEntity[];
    visualization: VisualizationEntity;
    options: VisualizationOptionsType;
    company?: CompanyEntity;
}
